import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { ASSET_POLL_INTERVAL_MS } from '@/hooks/useDataPolling/pollingConstants';
import { api, getBaseUrl } from '@/services/api';
import { AssetState } from '@/api/generatedApi';
import { isLabelPending } from '@/utils/assets';
import { Box, CircularProgress, styled } from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { AssetSidebar } from '../AssetSidebar';
import { DependencyDetailPopover } from './DependencyDetailPopover';
import { AssetErrorMessage } from './AssetErrorMessage';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { useCriticalGeometryPut } from '@/hooks/assetViewer/useCriticalGeometryPut';
import { useSelectionSync } from '@/hooks/assetViewer/useSelectionSync';
import { useThemeMode } from '@/hooks/useThemeMode';
import { DependencyContext } from '../AssetSidebar/DependencyTree/DependencyTreeView';

const MainContentArea = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  overflow: 'hidden',
});

const AssetView = styled(Box)(({ theme }) => ({
  flexDirection: 'column',
  flex: '2 1 auto',
  minWidth: '200px',
  display: 'flex',
  backgroundColor: theme.palette.background.default,
}));

const AssetIframe = styled('iframe')({
  width: '100%',
  height: '100%',
  border: 0,
});

const ZeroStateBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
});

export const AssetViewer = ({ assetId }: { assetId?: string }) => {
  const { selectedTenantId: assetTenantId } = useSearchFilters();
  const { resolvedMode } = useThemeMode();

  const {
    asset,
    error: assetError,
    isLoading: assetIsLoading,
  } = useGetAsset({
    assetId: assetId ?? '',
    assetTenantId,
    refetchInterval: query => {
      const fetchedAsset = query.state.data?.data.asset;
      if (fetchedAsset?.state === AssetState.Indexing || isLabelPending(fetchedAsset)) return ASSET_POLL_INTERVAL_MS;
      return false;
    },
  });
  const [error, setError] = useState<string>();
  const iframeReference = useRef<HTMLIFrameElement>(null);
  const contentReference = useRef<HTMLDivElement>(null);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [selectedNodeNames, setSelectedNodeNames] = useState<string[]>([]);
  const [selectedDependencyContext, setSelectedDependencyContext] = useState<DependencyContext>();
  const { put: handleReprocessCriticalGeometry, isPutInFlight: isReprocessInFlight } = useCriticalGeometryPut({
    assetId,
    tenantId: assetTenantId,
    iframeRef: iframeReference,
    assetType: asset?.type,
  });

  const isUnsupported = asset?.state === AssetState.Unsupported;
  const isIndexing = asset?.state === AssetState.Indexing;

  const handlePopoverClose = () => {
    setSelectedAssetIds([]);
    setSelectedNodeNames([]);
    setSelectedDependencyContext(undefined);
  };

  const { sendSelectionToViewer, sendClearToViewer } = useSelectionSync({
    iframeRef: iframeReference,
    onViewerSelect: (assetIds: string[], nodeNames: string[]) => {
      setSelectedNodeNames(nodeNames);
      if (assetIds.length > 0) {
        setSelectedAssetIds(assetIds);
        setSelectedDependencyContext(undefined);
      }
    },
    onViewerClear: () => {
      handlePopoverClose();
    },
  });

  const handleTreeItemClick = (itemId: string, context?: DependencyContext) => {
    setSelectedAssetIds(itemId ? [itemId] : []);
    setSelectedNodeNames([]);
    setSelectedDependencyContext(context);
    if (itemId) {
      const paths = [context?.dependencyPath, context?.assetPath].filter(Boolean) as string[];
      sendSelectionToViewer([itemId], paths);
    } else {
      sendClearToViewer();
    }
  };

  const fetchViewer = async () => {
    setError(undefined);
    try {
      const apiLocation = getBaseUrl();

      const {
        data: { token },
      } = await api.viewer.viewerToken();

      const iframeSource = `${apiLocation}/tenants/${assetTenantId}/viewer/asset?token=${token}&assetId=${assetId}&theme=${resolvedMode}&parentOrigin=${encodeURIComponent(globalThis.location.origin)}`;

      if (iframeReference.current) {
        iframeReference.current.src = iframeSource;
      }
    } catch (error) {
      setError(error instanceof AxiosError ? error.message : 'Error fetching viewer');
    }
  };

  useEffect(() => {
    if (assetId && !assetError && !assetIsLoading && !isUnsupported && !isIndexing) {
      fetchViewer();
    }
  }, [assetId, assetTenantId, assetError, assetIsLoading, asset?.state]);

  useEffect(() => {
    const iframe = iframeReference.current;
    if (!iframe?.contentWindow || !iframe.src) return;
    const targetOrigin = new URL(iframe.src).origin;
    iframe.contentWindow.postMessage({ type: 'physna:theme-mode', mode: resolvedMode }, targetOrigin);
  }, [resolvedMode]);

  if (assetIsLoading) {
    return (
      <ZeroStateBox>
        <CircularProgress />
      </ZeroStateBox>
    );
  }

  if (assetError) {
    return <AssetErrorMessage errorMessage={assetError.message} />;
  }

  return (
    <MainContentArea>
      <AssetSidebar
        asset={asset}
        onTreeItemClick={handleTreeItemClick}
        onReprocessCriticalGeometry={handleReprocessCriticalGeometry}
        isReprocessCriticalGeometryDisabled={!assetId || !assetTenantId || isReprocessInFlight}
        highlightedAssetIds={selectedAssetIds}
        highlightedNodeNames={selectedNodeNames}
      />
      <AssetView ref={contentReference}>
        {isUnsupported && <AssetErrorMessage assetState={AssetState.Unsupported} />}
        {!isUnsupported && isIndexing && <AssetErrorMessage assetState={AssetState.Indexing} />}
        {!isUnsupported && !isIndexing && (
          <>
            <AssetIframe
              key={assetId}
              ref={iframeReference}
              title='3D Model Viewer'
              sandbox='allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox'
              referrerPolicy='no-referrer'
            />
            {error && <div>{error}</div>}
          </>
        )}
        <DependencyDetailPopover
          open={selectedAssetIds.length > 0}
          anchorEl={contentReference.current}
          selectedTreeItemId={selectedAssetIds.at(-1) ?? ''}
          dependencyContext={selectedDependencyContext}
          onClose={handlePopoverClose}
        />
      </AssetView>
    </MainContentArea>
  );
};
